/**
 * Certification Documents Management Hook
 * 
 * Handles file upload, retrieval, and deletion for vendor certification documents.
 * Provides secure storage of PDF and image files in Supabase Storage with
 * validation, signed URLs, and proper file organization.
 * 
 * **File Requirements:**
 * - Allowed types: PDF, JPEG, PNG, JPG
 * - Maximum size: 10MB per file
 * - Storage bucket: 'documents'
 * - Path structure: certifications/{vendor_profile_id}/{certification_id}/{filename}
 * 
 * **Upload Process:**
 * 1. Client validates file type and size
 * 2. Generate unique filename with timestamp
 * 3. Upload to Supabase Storage
 * 4. Return storage path for database reference
 * 5. Admin reviews document via signed URL
 * 
 * **Security Features:**
 * - File type validation (prevents executable uploads)
 * - Size limit enforcement (prevents storage abuse)
 * - Signed URLs with 1-hour expiry (prevents unauthorized access)
 * - Organization-scoped access control
 * - Audit trail of uploads and deletions
 * 
 * **Storage Organization:**
 * - /certifications/{vendorProfileId}/{certificationId}/{timestamp}-{random}.ext
 * - /certifications/{vendorProfileId}/temp/{timestamp}-{random}.ext (for draft uploads)
 * 
 * **Key Features:**
 * - File validation before upload
 * - Secure file storage
 * - Signed URL generation for viewing
 * - File deletion capability
 * - Upload progress tracking
 * - Toast notifications for feedback
 * 
 * **Use Cases:**
 * - Vendor certification uploads
 * - Admin document review
 * - Certification verification
 * - Compliance documentation
 * - Document lifecycle management
 * 
 * @returns {Object} Document management functions
 * @returns {boolean} uploading - Upload in progress state
 * @returns {Function} validateFile - Validate file before upload
 * @returns {Function} uploadCertificationDocument - Upload document to storage
 * @returns {Function} getDocumentUrl - Get signed URL for viewing
 * @returns {Function} deleteCertificationDocument - Delete document from storage
 * 
 * @example
 * // Upload certification document
 * const { 
 *   uploading, 
 *   validateFile, 
 *   uploadCertificationDocument 
 * } = useCertificationDocuments();
 * 
 * const handleFileUpload = async (file: File) => {
 *   const error = validateFile(file);
 *   if (error) {
 *     toast({ title: 'Invalid file', description: error });
 *     return;
 *   }
 * 
 *   const path = await uploadCertificationDocument(
 *     file,
 *     vendorProfileId,
 *     certificationId
 *   );
 * 
 *   if (path) {
 *     // Save path to database
 *     await saveCertification({ document_url: path });
 *   }
 * };
 * 
 * @example
 * // View certification document
 * const { getDocumentUrl } = useCertificationDocuments();
 * 
 * const handleViewDocument = async (storagePath: string) => {
 *   const url = await getDocumentUrl(storagePath);
 *   if (url) {
 *     window.open(url, '_blank');
 *   }
 * };
 * 
 * @example
 * // Delete certification document
 * const { deleteCertificationDocument } = useCertificationDocuments();
 * 
 * const handleDelete = async (storagePath: string) => {
 *   const success = await deleteCertificationDocument(storagePath);
 *   if (success) {
 *     // Update database to remove reference
 *     await updateCertification({ document_url: null });
 *   }
 * };
 */

import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];

export const useCertificationDocuments = () => {
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Only PDF, JPG, and PNG files are allowed';
    }

    if (file.size > MAX_FILE_SIZE) {
      return 'File size must be less than 10MB';
    }

    return null;
  };

  const uploadCertificationDocument = async (
    file: File,
    vendorProfileId: string,
    certificationId?: string
  ): Promise<string | null> => {
    const validationError = validateFile(file);
    if (validationError) {
      toast({
        title: 'Invalid File',
        description: validationError,
        variant: 'destructive',
      });
      return null;
    }

    try {
      setUploading(true);

      // Generate unique file path
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = certificationId
        ? `certifications/${vendorProfileId}/${certificationId}/${fileName}`
        : `certifications/${vendorProfileId}/temp/${fileName}`;

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        throw uploadError;
      }

      toast({
        title: 'Success',
        description: 'Document uploaded successfully',
      });

      return filePath;
    } catch (error: any) {
      console.error('Error uploading document:', error);
      toast({
        title: 'Upload Failed',
        description: error.message || 'Failed to upload document',
        variant: 'destructive',
      });
      return null;
    } finally {
      setUploading(false);
    }
  };

  const getDocumentUrl = async (storagePath: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .createSignedUrl(storagePath, 3600); // 1 hour expiry

      if (error) throw error;
      return data.signedUrl;
    } catch (error: any) {
      console.error('Error getting document URL:', error);
      toast({
        title: 'Error',
        description: 'Failed to load document',
        variant: 'destructive',
      });
      return null;
    }
  };

  const deleteCertificationDocument = async (storagePath: string): Promise<boolean> => {
    try {
      const { error } = await supabase.storage
        .from('documents')
        .remove([storagePath]);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Document deleted successfully',
      });

      return true;
    } catch (error: any) {
      console.error('Error deleting document:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete document',
        variant: 'destructive',
      });
      return false;
    }
  };

  return {
    uploading,
    validateFile,
    uploadCertificationDocument,
    getDocumentUrl,
    deleteCertificationDocument,
  };
};
